<script setup lang="ts">
import { computed } from 'vue';
import { useOrderEditStore } from '@/stores/orderEdit';
import { fillText, useEditTexts } from '@/composables/useEditTexts';
import EditContact from './edit-contact.vue';
import EditLineCard from './edit-line-card.vue';

const { section, frozen = false } = defineProps<{ section: 'menus' | 'drinks' | 'options'; frozen?: boolean }>();

const store = useOrderEditStore();
const t = useEditTexts();

const lines = computed(() => store.view?.sections[section] ?? []);
const title = computed(() =>
  section === 'menus' ? t.edit_section_menus : section === 'drinks' ? t.edit_section_drinks : t.edit_section_options,
);

// Spec §9.1: each section shows why it can't change, or can't grow.
const notices = computed<string[]>(() => {
  const v = store.view;
  if (!v || section !== 'menus') return [];
  if (v.menusLock === 'dishes_chosen') return [t.edit_menus_locked_dishes];
  if (v.menusLock === 'capacity_unknown') return [t.edit_menus_locked_capacity];
  const out: string[] = [];
  if (store.capacityReached && v.capacity !== null) out.push(fillText(t.edit_capacity_reached, { capacity: v.capacity }));
  if (store.mealsBelowHeadcount) out.push(fillText(t.edit_menus_min, { headcount: v.headcount }));
  return out;
});

const showContact = computed(
  () => section === 'menus' && (store.capacityReached || store.view?.menusLock === 'capacity_unknown'),
);
</script>

<template>
  <div v-if="lines.length > 0" class="order-edit-section" :data-testid="`order-edit-section-${section}`">
    <div class="bpa-front-module-heading">{{ title }}</div>
    <p
      v-for="notice in notices"
      :key="notice"
      class="order-edit-section__notice"
      data-testid="order-edit-section-notice"
    >
      {{ notice }}
    </p>
    <edit-contact v-if="showContact" />
    <div class="bpa-front-module--service-items-row">
      <edit-line-card v-for="line in lines" :key="line.product" :line="line" :frozen="frozen" />
    </div>
  </div>
</template>

<style scoped>
.order-edit-section {
  margin-bottom: 24px;
}

.order-edit-section__notice {
  margin: 4px 0 12px;
  font-style: italic;
}
</style>
